/**
 * CancelSubscriptionPage — 3-step in-app cancellation flow (SOS-334).
 *
 *   Step 1: optional reason (radio list of 5 categorical options)
 *   Step 2: confirmation with destructive CTA
 *   Step 3: success state (with auto-redirect option to Manage)
 *
 * State-machine via local state — no nested routes, navigation is intentional
 * via "← Back" and step-controlled CTAs only. This matches the spec ("Changed
 * my mind" returns to Manage, "Cancel" in step 2 returns to Manage).
 *
 * Edge cases handled:
 *   - Past Due → backend automatically switches to immediate cancellation.
 *     Step 2 detects past_due via displayState and adjusts copy.
 *   - No active subscription → redirects back to manage.
 *   - Network / Stripe failure → step stays open, error toast surfaces message.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/contexts/I18nContext';
import { useToast } from '@/shared/hooks/use-toast';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useCancelSubscriptionAtPeriodEnd } from '../hooks/useManageSubscription';
import { StepDots } from '../components/StepDots';
import type { CancellationReason } from '../services/subscriptionApi';

/** Categorical reasons — must match backend allow-list (CHECK constraint). */
const REASON_OPTIONS: { value: CancellationReason; labelKey: string }[] = [
  { value: 'too_little_usage',           labelKey: 'premium.cancel.reason.too_little_usage' },
  { value: 'not_worth_price',            labelKey: 'premium.cancel.reason.not_worth_price' },
  { value: 'roll_reviews_not_reaching',  labelKey: 'premium.cancel.reason.roll_reviews_not_reaching' },
  { value: 'missing_feature',            labelKey: 'premium.cancel.reason.missing_feature' },
  { value: 'other',                      labelKey: 'premium.cancel.reason.other' },
];

type CancelStep = 'reason' | 'confirm' | 'done';

function formatDate(date: Date | null): string {
  if (!date) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CancelSubscriptionPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { toast } = useToast();
  const { subscription, isPro, displayState, isLoading, refetch } = useSubscriptionStatus();
  const cancelMutation = useCancelSubscriptionAtPeriodEnd();

  const [step, setStep] = useState<CancelStep>('reason');
  const [reason, setReason] = useState<CancellationReason | null>(null);

  const isPastDue = displayState === 'past_due';
  const periodEndLabel = useMemo(
    () => formatDate(subscription?.currentPeriodEnd ?? null),
    [subscription?.currentPeriodEnd],
  );

  // Guard: if no subscription to cancel, redirect to manage. We render the
  // layout shell while we wait so we never flash empty content.
  if (!isLoading && (!isPro || !subscription)) {
    navigate('/settings/subscription', { replace: true });
  }

  const onBack = () => {
    if (step === 'reason') return navigate('/settings/subscription', { replace: true });
    if (step === 'confirm') return setStep('reason');
    // done step: replace so back-stack doesn't re-enter the cancel flow
    return navigate('/settings/subscription', { replace: true });
  };

  const onConfirm = () => {
    cancelMutation.mutate(
      { reason },
      {
        onSuccess: async () => {
          await refetch();
          setStep('done');
        },
        onError: (err) => {
          toast({
            title: t('premium.cancel.error_title'),
            description: err instanceof Error ? err.message : undefined,
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <SubPageLayout title={t('premium.cancel.title')} hideBottomNav onBack={onBack}>
      <div className="max-w-md mx-auto pt-2 pb-12 flex flex-col gap-5">
        <StepDots
          total={3}
          currentIndex={step === 'reason' ? 0 : step === 'confirm' ? 1 : 2}
          ariaLabel={t('premium.cancel.step_aria')}
          className="self-center"
        />

        {step === 'reason' && (
          <ReasonStep
            reason={reason}
            onChange={setReason}
            onContinue={() => setStep('confirm')}
            onAbort={() => navigate('/settings/subscription', { replace: true })}
          />
        )}

        {step === 'confirm' && (
          <ConfirmStep
            isPastDue={isPastDue}
            periodEndLabel={periodEndLabel}
            busy={cancelMutation.isPending}
            onConfirm={onConfirm}
            onAbort={() => setStep('reason')}
          />
        )}

        {step === 'done' && (
          <DoneStep
            isPastDue={isPastDue}
            periodEndLabel={periodEndLabel}
            onClose={() => navigate('/', { replace: true })}
            onManage={() => navigate('/settings/subscription', { replace: true })}
          />
        )}
      </div>
    </SubPageLayout>
  );
}

// ── Step 1: Reason capture ───────────────────────────────────────────────────

function ReasonStep({
  reason,
  onChange,
  onContinue,
  onAbort,
}: {
  reason: CancellationReason | null;
  onChange: (value: CancellationReason | null) => void;
  onContinue: () => void;
  onAbort: () => void;
}) {
  const { t } = useI18n();
  return (
    <>
      <header className="space-y-2">
        <h2 className="text-xl font-bold text-white">{t('premium.cancel.reason_title')}</h2>
        <p className="text-sm text-white/60">{t('premium.cancel.reason_subtitle')}</p>
      </header>

      <RadioGroup
        value={reason ?? ''}
        onValueChange={(v) => onChange(v as CancellationReason)}
        className="flex flex-col gap-2"
      >
        {REASON_OPTIONS.map((opt) => (
          <Label
            key={opt.value}
            htmlFor={`reason-${opt.value}`}
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 cursor-pointer hover:bg-white/10 transition-colors data-[state=checked]:border-[#D72E16]"
            data-state={reason === opt.value ? 'checked' : 'unchecked'}
          >
            <RadioGroupItem
              value={opt.value}
              id={`reason-${opt.value}`}
              className="border-white/40"
            />
            <span className="text-sm text-white">{t(opt.labelKey)}</span>
          </Label>
        ))}
      </RadioGroup>

      <div className="flex flex-col gap-2 pt-2">
        <Button data-testid="cancel-step1-continue" variant="destructive" onClick={onContinue} className="w-full">
          {t('premium.cancel.continue_anyway')}
        </Button>
        <Button data-testid="cancel-step1-abort" variant="ghost" onClick={onAbort} className="w-full text-white/70">
          {t('premium.cancel.changed_my_mind')}
        </Button>
      </div>
    </>
  );
}

// ── Step 2: Confirmation ─────────────────────────────────────────────────────

function ConfirmStep({
  isPastDue,
  periodEndLabel,
  busy,
  onConfirm,
  onAbort,
}: {
  isPastDue: boolean;
  periodEndLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onAbort: () => void;
}) {
  const { t } = useI18n();

  const bodyKey = isPastDue ? 'premium.cancel.confirm_body_past_due' : 'premium.cancel.confirm_body';

  return (
    <>
      <header className="space-y-2">
        <h2 className="text-xl font-bold text-white">{t('premium.cancel.confirm_title')}</h2>
        <p className="text-sm text-white/70 leading-relaxed">
          {t(bodyKey, { date: periodEndLabel })}
        </p>
      </header>

      <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/60 leading-relaxed">
        {t('premium.cancel.confirm_note')}
      </div>

      <div
        data-testid="cancel-in-progress-reviews-warning"
        className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300 leading-relaxed"
      >
        {t('premium.cancel.in_progress_reviews_warning')}
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <Button
          data-testid="cancel-step2-confirm"
          variant="destructive"
          disabled={busy}
          onClick={onConfirm}
          className="w-full"
        >
          {busy ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              {t('premium.cancel.working')}
            </span>
          ) : (
            t('premium.cancel.confirm_cta')
          )}
        </Button>
        <Button data-testid="cancel-step2-abort" variant="ghost" disabled={busy} onClick={onAbort} className="w-full text-white/70">
          {t('premium.cancel.confirm_abort')}
        </Button>
      </div>
    </>
  );
}

// ── Step 3: Done ─────────────────────────────────────────────────────────────

function DoneStep({
  isPastDue,
  periodEndLabel,
  onClose,
  onManage,
}: {
  isPastDue: boolean;
  periodEndLabel: string;
  onClose: () => void;
  onManage: () => void;
}) {
  const { t } = useI18n();

  return (
    <>
      <div data-testid="cancel-done-step" className="flex flex-col items-center text-center gap-4 pt-6">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
          <Check size={28} className="text-emerald-400" />
        </div>
        <h2 className="text-xl font-bold text-white">{t('premium.cancel.done_title')}</h2>
        <p className="text-sm text-white/70 leading-relaxed max-w-xs">
          {isPastDue
            ? t('premium.cancel.done_body_past_due')
            : t('premium.cancel.done_body', { date: periodEndLabel })}
        </p>
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <Button variant="primary" onClick={onClose} className="w-full">
          {t('premium.cancel.done_back_home')}
        </Button>
        <Button variant="outline" onClick={onManage} className="w-full">
          {t('premium.cancel.done_view_manage')}
        </Button>
      </div>
    </>
  );
}
